import type { AuthClientKind, AuthFlow, AuthProviderConfig } from '@/composables/auth/types'
import { isTailnetHost } from '@/services/tailnet'

export const getSilentAuthRedirectUri = (origin: string) =>
  `${origin.replace(/\/$/, '')}/silent-auth-callback.html`

class SettingsService {
  get isDevelopment() {
    return import.meta.env.MODE === 'development'
  }

  get orbvizDomain() {
    return import.meta.env.VITE_ORBVIZ_DOMAIN ?? 'orbviz.com'
  }

  get defaultRedirectUrl() {
    if (
      this.isDevelopment &&
      typeof window !== 'undefined' &&
      isTailnetHost(window.location.hostname, import.meta.env.VITE_TAILNET_DNS_SUFFIX)
    ) {
      return `${window.location.origin}/dashboard/`
    }
    return (
      import.meta.env.VITE_DEFAULT_REDIRECT_URL ?? `https://default.${this.orbvizDomain}/dashboard`
    )
  }

  get allowedRedirectOrigins() {
    return (import.meta.env.VITE_ALLOWED_REDIRECT_ORIGINS ?? '')
      .split(/[,\s]+/)
      .map((origin: string) => origin.trim())
      .filter(Boolean)
  }

  get identityApiBaseUrl() {
    if (this.isDevelopment) {
      return typeof window !== 'undefined'
        ? `${window.location.origin}/api/iam`
        : 'http://localhost:8085/api/iam'
    }
    const azureIam = import.meta.env.VITE_IAM_API_BASE_URL
    if (azureIam) {
      return azureIam.replace(/\/$/, '')
    }
    return `https://global.${this.orbvizDomain}/api/iam`
  }

  get authRedirectUri() {
    const origin = typeof window === 'undefined' ? '' : window.location.origin
    if (
      this.isDevelopment &&
      typeof window !== 'undefined' &&
      isTailnetHost(window.location.hostname, import.meta.env.VITE_TAILNET_DNS_SUFFIX)
    ) {
      return `${origin}/callback`
    }
    return import.meta.env.VITE_ENTRA_REDIRECT_URI ?? `${origin}/callback`
  }

  get silentAuthRedirectUri() {
    const origin = typeof window === 'undefined' ? '' : window.location.origin
    return getSilentAuthRedirectUri(origin)
  }

  get authPostLogoutRedirectUri() {
    const origin = typeof window === 'undefined' ? '' : window.location.origin
    if (
      this.isDevelopment &&
      typeof window !== 'undefined' &&
      isTailnetHost(window.location.hostname, import.meta.env.VITE_TAILNET_DNS_SUFFIX)
    ) {
      return `${origin}/signout-callback`
    }
    return import.meta.env.VITE_ENTRA_POST_LOGOUT_REDIRECT_URI ?? `${origin}/signout-callback`
  }

  get entraWorkforceAuthority() {
    return import.meta.env.VITE_ENTRA_WORKFORCE_AUTHORITY
  }

  get entraWorkforceClientId() {
    return import.meta.env.VITE_ENTRA_WORKFORCE_CLIENT_ID
  }

  get entraExternalAuthority() {
    return import.meta.env.VITE_ENTRA_EXTERNAL_AUTHORITY
  }

  get entraExternalClientId() {
    return import.meta.env.VITE_ENTRA_EXTERNAL_CLIENT_ID
  }

  get entraExternalSignupAuthority() {
    return import.meta.env.VITE_ENTRA_EXTERNAL_SIGNUP_AUTHORITY ?? this.entraExternalAuthority
  }

  get entraExternalPasswordAuthority() {
    return this.entraExternalSignupAuthority
  }

  get entraExternalPasswordClientId() {
    return import.meta.env.VITE_ENTRA_EXTERNAL_SIGNUP_PASSWORD_CLIENT_ID
  }

  get entraWorkforceScopes() {
    return this.parseScopes(import.meta.env.VITE_ENTRA_WORKFORCE_SCOPES)
  }

  get entraExternalScopes() {
    return this.parseScopes(import.meta.env.VITE_ENTRA_EXTERNAL_SCOPES)
  }

  /**
   * Fallback Azure AD identity provider (resource) id for External ID user flows
   * when IAM `entra-external-signin-config` is unavailable or omits the value.
   * Same value as IAM `Entra:ExternalAzureAdIdentityProviderId` / `azureAdIdentityProviderId` in the API.
   */
  get entraExternalAzureAdIdentityProviderIdFallback(): string | undefined {
    const raw = import.meta.env.VITE_ENTRA_EXTERNAL_AZUREAD_IDP as string | undefined
    const trimmed = raw?.trim()
    return trimmed || undefined
  }

  getAuthConfig(flow: AuthFlow): AuthProviderConfig {
    switch (flow) {
      case 'workforce':
        return this.buildAuthConfig(
          'workforce',
          this.entraWorkforceAuthority,
          this.entraWorkforceClientId,
          this.entraWorkforceScopes,
        )
      case 'external-signup':
        return this.buildAuthConfig(
          'external',
          this.entraExternalSignupAuthority,
          this.entraExternalClientId,
          this.entraExternalScopes,
        )
      case 'external-signup-password':
        return this.buildAuthConfig(
          'externalPassword',
          this.entraExternalPasswordAuthority,
          this.entraExternalPasswordClientId,
          this.entraExternalScopes,
        )
      case 'external-signin-password':
        return this.buildAuthConfig(
          'externalPassword',
          this.entraExternalPasswordAuthority,
          this.entraExternalPasswordClientId,
          this.entraExternalScopes,
        )
      case 'external-signin':
      default:
        return this.buildAuthConfig(
          'external',
          this.entraExternalAuthority,
          this.entraExternalClientId,
          this.entraExternalScopes,
        )
    }
  }

  getClientConfig(clientKind: AuthClientKind): AuthProviderConfig {
    const baseConfig =
      clientKind === 'workforce'
        ? this.getAuthConfig('workforce')
        : clientKind === 'externalPassword'
          ? this.getAuthConfig('external-signup-password')
          : this.getAuthConfig('external-signin')

    return {
      ...baseConfig,
      knownAuthorities: this.getKnownAuthoritiesForClient(clientKind),
    }
  }

  isFlowEnabled(flow: AuthFlow) {
    try {
      this.getAuthConfig(flow)
      return true
    } catch {
      return false
    }
  }

  getConfiguredClientKinds(): AuthClientKind[] {
    return (['external', 'workforce', 'externalPassword'] as const).filter((clientKind) => {
      try {
        this.getClientConfig(clientKind)
        return true
      } catch {
        return false
      }
    })
  }

  private buildAuthConfig(
    clientKind: AuthClientKind,
    authority: string | undefined,
    clientId: string | undefined,
    scopes: string[],
  ): AuthProviderConfig {
    if (!authority || !clientId) {
      throw new Error('Azure Entra authentication is not fully configured for this environment.')
    }

    return {
      clientKind,
      authority,
      clientId,
      redirectUri: this.authRedirectUri,
      postLogoutRedirectUri: this.authPostLogoutRedirectUri,
      scopes,
      knownAuthorities: this.getKnownAuthorities(authority),
    }
  }

  private getKnownAuthorities(authority: string) {
    const hostname = this.getAuthorityHostname(authority)

    if (!hostname || hostname === 'login.microsoftonline.com') {
      return []
    }

    return [hostname]
  }

  private getKnownAuthoritiesForClient(clientKind: AuthClientKind) {
    if (clientKind === 'workforce') {
      return this.getKnownAuthorities(this.getAuthConfig('workforce').authority)
    }

    if (clientKind === 'externalPassword') {
      return this.getKnownAuthorities(this.getAuthConfig('external-signup-password').authority)
    }

    return Array.from(
      new Set(
        [
          this.entraExternalAuthority,
          this.entraExternalSignupAuthority,
        ]
          .flatMap((authority) => (authority ? this.getKnownAuthorities(authority) : []))
          .filter(Boolean),
      ),
    )
  }

  private getAuthorityHostname(authority: string) {
    try {
      return new URL(authority).hostname
    } catch {
      return null
    }
  }

  private parseScopes(value: string | undefined): string[] {
    const baseScopes = ['openid', 'profile', 'offline_access']
    const rawScopes = (value ?? import.meta.env.VITE_ENTRA_API_SCOPES ?? '')
      .split(/[,\s]+/)
      .map((scope: string) => scope.trim())
      .filter(Boolean)

    return Array.from(new Set([...baseScopes, ...rawScopes]))
  }
}

export const settingsService = new SettingsService()
